import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

// SVG flags (CZ, EN=GB, ES). Rendered at 20x14 by default.
function FlagCZ({ size = 20 }) {
  const h = (size * 14) / 20;
  return (
    <svg width={size} height={h} viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: 2, display: "block" }}>
      <rect width="20" height="7" fill="#ffffff" />
      <rect y="7" width="20" height="7" fill="#d7141a" />
      <polygon points="0,0 9,7 0,14" fill="#11457e" />
    </svg>
  );
}
function FlagEN({ size = 20 }) {
  const h = (size * 14) / 20;
  return (
    <svg width={size} height={h} viewBox="0 0 60 42" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: 2, display: "block" }}>
      <rect width="60" height="42" fill="#012169" />
      <path d="M0,0 L60,42 M60,0 L0,42" stroke="#ffffff" strokeWidth="6" />
      <path d="M30,0 L30,42 M0,21 L60,21" stroke="#ffffff" strokeWidth="10" />
      <path d="M30,0 L30,42 M0,21 L60,21" stroke="#c8102e" strokeWidth="6" />
      <path d="M0,0 L60,42 M60,0 L0,42" stroke="#c8102e" strokeWidth="2.5" strokeDasharray="0 21 21 21" />
    </svg>
  );
}
function FlagES({ size = 20 }) {
  const h = (size * 14) / 20;
  return (
    <svg width={size} height={h} viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: 2, display: "block" }}>
      <rect width="20" height="14" fill="#aa151b" />
      <rect y="3.5" width="20" height="7" fill="#f1bf00" />
    </svg>
  );
}

const FLAGS = { cs: FlagCZ, en: FlagEN, es: FlagES };
const LABELS = { cs: "Čeština", en: "English", es: "Español" };

export default function LocaleSwitcher({ current, setLocale, locales, className = "" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const Current = FLAGS[current] || FlagCZ;

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="locale-toggle"
        aria-label="Change language"
        aria-expanded={open}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-[#efeadd]/60 transition-colors duration-200"
      >
        <Current size={20} />
        <ChevronDown size={12} strokeWidth={1.75} className={`text-[#6f6f6a] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 min-w-[150px] bg-[#fdfcf9]/95 backdrop-blur-md border border-[#e6e1d6] rounded-md shadow-[0_20px_50px_-30px_rgba(37,37,37,0.35)] overflow-hidden z-50"
          data-testid="locale-menu"
        >
          {locales.map((l) => {
            const Flag = FLAGS[l];
            const isActive = l === current;
            return (
              <button
                key={l}
                type="button"
                onClick={() => {
                  setLocale(l);
                  setOpen(false);
                }}
                data-testid={`locale-option-${l}`}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left transition-colors duration-200 ${
                  isActive ? "bg-[#f4f0e9]" : "hover:bg-[#f4f0e9]/60"
                }`}
              >
                <Flag size={20} />
                <span className={`text-[13px] tracking-wide ${isActive ? "text-[#252525]" : "text-[#4a4a45]"}`}>
                  {LABELS[l]}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
